<script lang="ts">
	import type { Mediator, SvelteAsr, i18n } from 'types/common'
	import type { InterchangeLookupFilters, InterchangeRecord } from 'utility/interchange-lookup'

	import Card from '@isoftdata/svelte-card'
	import Table, { Td, type Column } from '@isoftdata/svelte-table'
	import Textarea from '@isoftdata/svelte-textarea'
	import Attachments from '@isoftdata/svelte-attachments'
	import Button from '@isoftdata/svelte-button'
	import Icon from '@isoftdata/svelte-icon'
	import { format as currencyFormat } from '@isoftdata/utility-currency'
	import interchangeLookup from 'utility/interchange-lookup'
	import { getContext } from 'svelte'

	interface Props {
		asr: SvelteAsr
		interchangeList?: Array<InterchangeRecord>
		isLoading?: boolean
		exactNotes?: string
		inexactNotes?: string
		isPicking?: boolean
		didSearch?: boolean
		interchangeClick?: (value: { interchange: InterchangeRecord }) => void
		inventorySearch?: (interchangeRecord: { inventoryTypeId: number; interchangeNumber: string }) => void
	}

	let {
		asr,
		interchangeList = $bindable([]),
		isLoading = false,
		exactNotes = '',
		inexactNotes = '',
		didSearch = false,
		isPicking = false,
		interchangeClick,
		inventorySearch,
	}: Props = $props()

	let selectedInterchange: InterchangeRecord | null = $state<InterchangeRecord | null>(null)
	let table: Table<(typeof displayInterchange)[number]> | undefined = $state()
	let currentSearchType: '' | 'GROUP' | 'APPLICATION' = $state('')
	let sortColumn: Column | undefined = $state()

	const { t: translate } = getContext<i18n>('i18next')
	const columns: Array<Column<(typeof displayInterchange)[number]>> = [
		{
			property: 'interchangeNumber',
			name: translate('common:interchangeNum', 'Interchange #'),
			sortType: 'ALPHA_NUM',
			defaultSortColumn: true,
		},
		{
			property: 'subInterchangeNumber',
			name: translate('common:subInterchange', 'Sub Interchange'),
			sortType: 'ALPHA_NUM',
		},
		{ property: 'make', name: translate('common:makeManuf', 'Make / Manuf.') },
		{ property: 'model', name: translate('common:model', 'Model') },
		{ property: 'yearRange', name: translate('common:year', 'Year'), numeric: true, sortType: 'ALPHA_NUM' },
		{ property: 'option', name: translate('common:option', 'Option') },
		{ property: 'side', name: translate('common:side', 'Side') },
		{ property: 'listPrice', name: translate('common:list', 'List'), numeric: true },
	]

	export async function search(filter: InterchangeLookupFilters, exactSearch = false) {
		currentSearchType = filter.interchangeNumber ? 'GROUP' : 'APPLICATION'
		isLoading = true
		didSearch = true

		try {
			interchangeList = await interchangeLookup(filter)

			return interchangeList
		} finally {
			isLoading = false
		}
	}

	export function show(list: Array<InterchangeRecord>, type: '' | 'GROUP' | 'APPLICATION') {
		interchangeList = list
		currentSearchType = type
	}

	async function showInterchangeDetails(interchange: InterchangeRecord) {
		console.log(interchange)
		selectedInterchange = interchange
		exactNotes = interchange.exactApplicationNotes.join('\n\n') || ''
		inexactNotes = interchange.inexactApplicationNotes.join('\n\n') || ''
	}

	const displayInterchange = $derived(
		interchangeList.map(interchange => {
			const { firstYear, lastYear } = interchange
			return {
				...interchange,
				yearRange: firstYear !== lastYear ? `${firstYear} - ${lastYear}` : firstYear.toString(),
			}
		}),
	)
	const sideSet = $derived(new Set(interchangeList.map(({ side }) => side)))
	const showSide = $derived(!(sideSet.size === 1 && sideSet.has('NA')))
	$effect(() => {
		table?.setColumnVisibility(['side'], showSide)
	})
	const inexactInterchangeNumber = $derived(selectedInterchange?.interchangeNumber || '')
	const exactInterchangeNumber = $derived(
		`${selectedInterchange?.interchangeNumber || ''}${selectedInterchange?.subInterchangeNumber}`,
	)
</script>

{#if isLoading}
	<!-- svelte-ignore a11y_missing_content -->
	<h1 class="text-center">
		<Icon
			icon="spinner"
			animate="spin"
		/>
	</h1>
{:else}
	<div class="card">
		<div class="card-body">
			{#if displayInterchange.length > 0}
				<div class="row">
					<div
						class={{
							'col-12 col-md-6 col-lg-8': selectedInterchange,
							col: !selectedInterchange,
						}}
					>
						<Table
							responsive
							columnHidingEnabled
							rows={displayInterchange}
							perPageCount={15}
							{columns}
							bind:sortColumn
							bind:this={table}
						>
							{#snippet children({ row })}
								<tr
									class="cursor-pointer"
									class:table-primary={row.id === selectedInterchange?.id}
									onclick={() => showInterchangeDetails(row)}
								>
									<Td
										stopPropagation
										property="interchangeNumber"
									>
										<div class="d-flex justify-content-between">
											<span>{row.interchangeNumber}</span>
											<Button
												outline
												color="info"
												size="sm"
												icon="magnifying-glass"
												title="Perform Interchange Search"
												onclick={event => {
													event.stopPropagation()
													interchangeClick?.({ interchange: row })
												}}
											/>
											<!-- TODO: enable this button once we have the interchange component working on part search -->
											<!-- <Button
												outline
												size="sm"
												icon="search"
												onclick={(e) => {
													e.preventDefault()
													inventorySearch(row)
												}}
												>{translate('interchange.showInventory', 'Show Inventory')}</Button
											> -->
										</div>
									</Td>
									<Td
										property="subInterchangeNumber
									">{row.subInterchangeNumber}</Td
									>
									<Td property="make">{row.make}</Td>
									<Td property="model">{row.model}</Td>
									<Td property="yearRange">{row.yearRange}</Td>
									<Td property="option">{row.option}</Td>
									<Td property="side">{row.side}</Td>
									<Td property="listPrice">{row.listPrice ? currencyFormat(row.listPrice) : ''}</Td>
								</tr>
							{/snippet}
						</Table>
					</div>
					{#if selectedInterchange}
						<div class="col-12 col-md-6 col-lg-4">
							<Card
								headerClass="h5"
								header="Interchange Details"
							>
								<div class="form-row">
									<div class="col-md-6">
										<Textarea
											readonly
											label="{translate('interchange.exactNotes', 'Exact Notes ')} ({exactInterchangeNumber})"
											rows={10}
											value={exactNotes}
										/>
									</div>
									<div class="col-md-6">
										<Textarea
											readonly
											label="{translate('interchange.inexactNotes', 'Inexact Notes')} ({inexactInterchangeNumber})"
											rows={10}
											value={inexactNotes}
										/>
									</div>
								</div>

								<hr />
								<!--
							filesAdded={file => fileAssociation && attachmentFns.onFilesAdded(file, fileAssociation)}
							filesDeleted={file => fileAssociation && attachmentFns.onFilesDeleted(file, fileAssociation)}
							publicChange={file => fileAssociation && attachmentFns.onPublicChange(file, fileAssociation)}
							rankChange={file => fileAssociation && attachmentFns.onRankChange(file, fileAssociation)}
						-->
								<Attachments
									modificationDisabled
									uploadDisabled
									fileList={selectedInterchange.attachments.map(file => {
										return {
											...file,
											...file.file,
											mimeType: file.file.mimetype,
										}
									})}
								/>
							</Card>
						</div>
					{/if}
				</div>
			{:else if didSearch}
				<h4>{translate('common:noResults', 'No Results')}</h4>
			{/if}
		</div>
	</div>
{/if}
